import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Button,
  TextField,
  Typography,
  Box,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import SelectField from '../inputs/SelectField';
import {
  FANCY_COLOR_INTENSITY_LIST,
  FANCY_COLOR_HUE_LIST,
  FANCY_COLOR_OVERTONE_LIST,
} from '../../constants/appConstants';

const DIAMOND_STATUS_OPTIONS = [
  { value: 'available', label: 'Available' },
  { value: 'reserved', label: 'Reserved' },
  { value: 'sold', label: 'Sold' },
  { value: 'onTheWay', label: 'On the Way' },
  { value: 'notForSell', label: 'Not for sell' },
  { value: 'deleted', label: 'Deleted' },
];

const DIAMOND_GRADE_OPTIONS = ['Excellent', 'Very Good', 'Good', 'Fair', 'Poor'];
const DIAMOND_TYPE_OPTIONS = ['Natural', 'CVD', 'HPHT'];
const LAB_OPTIONS = ['GIA', 'IGI', 'HDR'];

function mapDiamondToForm(d) {
  return {
    colorCategory: d.colorCategory || 'standard',
    certificateNumber: d.certificateNumber,
    sku: d.sku ?? '',
    shipmentId: d.shipmentId ?? '',
    shipmentItemID: d.shipmentItemID ?? '',
    shape: d.shape || '',
    carat: d.carat ?? '',
    length: d.length ?? '',
    width: d.width ?? '',
    height: d.height ?? '',
    color: d.color || '',
    fancyIntensity: d.fancyColor?.intensity || '',
    fancyHue: d.fancyColor?.hue || '',
    fancyOvertone: d.fancyColor?.overtone || '',
    clarity: d.clarity || '',
    cut: d.cut || '',
    polish: d.polish || '',
    symmetry: d.symmetry || '',
    lab: d.lab || '',
    diamondType: d.diamondType || '',
    basePrice: d.basePrice ?? '',
    buyPricePerCarat: d.buyPricePerCarat ?? '',
    status: d.status || 'available',
  };
}

/**
 * Edit certified diamond dialog with local form state so typing does not re-render the inventory page.
 */
export default function EditDiamondDialog({
  open,
  diamondId,
  onClose,
  onSaved,
  formShapeList = [],
  filterOptions = {},
  isInternal = false,
  canSeeBuyPrices = false,
  isMobile = false,
  colorCategory = 'standard',
}) {
  const { t } = useTranslation();
  const defaultIsFancy = colorCategory === 'fancy';
  const [form, setForm] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !diamondId) {
      setForm(null);
      setError('');
      return undefined;
    }
    let cancelled = false;
    setForm(null);
    setError('');
    (async () => {
      try {
        const res = await axios.get(`/api/diamonds/${diamondId}`);
        if (!cancelled) setForm(mapDiamondToForm(res.data));
      } catch (err) {
        if (!cancelled) {
          setError(err.response?.data?.error || 'Failed to load diamond');
        }
      }
    })();
    return () => { cancelled = true; };
  }, [open, diamondId]);

  const setField = useCallback((key, value) => {
    setForm((prev) => (prev ? { ...prev, [key]: value } : prev));
  }, []);

  const isFancyForm = form?.colorCategory === 'fancy' || (form && defaultIsFancy);

  const intensityOptions = useMemo(() => {
    const fromApi = filterOptions.fancyIntensities || [];
    const list = fromApi.length > 0 ? fromApi : FANCY_COLOR_INTENSITY_LIST;
    return list.map((v) => ({ value: v, label: v }));
  }, [filterOptions.fancyIntensities]);

  const hueOptions = useMemo(() => {
    const fromApi = filterOptions.fancyHues || [];
    const list = fromApi.length > 0 ? fromApi : FANCY_COLOR_HUE_LIST;
    return list.map((v) => ({ value: v, label: v }));
  }, [filterOptions.fancyHues]);

  const overtoneOptions = useMemo(() => {
    const fromApi = filterOptions.fancyOvertones || [];
    const list = fromApi.length > 0 ? fromApi : FANCY_COLOR_OVERTONE_LIST;
    return list.map((v) => ({ value: v, label: v }));
  }, [filterOptions.fancyOvertones]);

  const shapeSelectOptions = useMemo(
    () => formShapeList.map((s) => {
      const shape = String(s).toUpperCase();
      return { value: shape, label: shape };
    }),
    [formShapeList],
  );

  const handleSave = async () => {
    if (!diamondId || !form || loading) return;
    setLoading(true);
    setError('');
    try {
      const isFancy = form.colorCategory === 'fancy';
      const payload = {
        colorCategory: isFancy ? 'fancy' : 'standard',
        sku: form.sku?.trim() || undefined,
        shipmentId: form.shipmentId?.trim() || undefined,
        shipmentItemID: form.shipmentItemID?.trim() || undefined,
        shape: (form.shape || '').trim().toUpperCase(),
        carat: Number(form.carat),
        length: form.length !== '' ? Number(form.length) : undefined,
        width: form.width !== '' ? Number(form.width) : undefined,
        height: form.height !== '' ? Number(form.height) : undefined,
        clarity: form.clarity,
        cut: form.cut || undefined,
        polish: form.polish || undefined,
        symmetry: form.symmetry || undefined,
        lab: form.lab,
        diamondType: form.diamondType || undefined,
        basePrice: Number(form.basePrice),
        status: form.status,
      };
      if (isFancy) {
        payload.fancyColor = {
          intensity: form.fancyIntensity,
          hue: form.fancyHue,
          ...(form.fancyOvertone ? { overtone: form.fancyOvertone } : {}),
        };
        payload.color = undefined;
      } else {
        payload.color = form.color;
        payload.fancyColor = undefined;
      }
      if (canSeeBuyPrices) {
        payload.buyPricePerCarat = form.buyPricePerCarat !== '' ? Number(form.buyPricePerCarat) : undefined;
      }
      await axios.put(`/api/diamonds/${diamondId}`, payload);
      onClose();
      onSaved?.();
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.errors?.[0]?.msg || 'Failed to save');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth fullScreen={isMobile}>
      <DialogTitle>{t('inventory.editDiamond')}</DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {!form && !error && <Typography sx={{ py: 2 }}>{t('common.loading')}</Typography>}
        {form && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField label="Certificate #" value={form.certificateNumber} disabled fullWidth size="small" />
            <TextField label="SKU (optional)" value={form.sku} onChange={(e) => setField('sku', e.target.value)} fullWidth size="small" />
            {isInternal && <TextField label="Shipment ID" value={form.shipmentId} onChange={(e) => setField('shipmentId', e.target.value)} fullWidth size="small" />}
            {isInternal && <TextField label="Shipment Item ID" value={form.shipmentItemID} onChange={(e) => setField('shipmentItemID', e.target.value)} fullWidth size="small" />}
            <Box display="flex" gap={1}>
              <SelectField
                label="Shape"
                size="small"
                sx={{ flex: 1 }}
                value={form.shape}
                onChange={(next) => setField('shape', next)}
                options={shapeSelectOptions}
                emptyLabel=""
              />
              <TextField label="Carat" type="number" value={form.carat} onChange={(e) => setField('carat', e.target.value)} inputProps={{ step: 0.01, min: 0 }} size="small" sx={{ flex: 1 }} />
              <SelectField
                label="Type"
                size="small"
                sx={{ flex: 1 }}
                value={form.diamondType}
                onChange={(next) => setField('diamondType', next)}
                options={DIAMOND_TYPE_OPTIONS.map((type) => ({ value: type, label: type }))}
                emptyLabel="—"
                includeEmptyOption
              />
            </Box>
            {isFancyForm ? (
              <>
                <SelectField
                  label={t('inventory.fancyColorIntensity')}
                  size="small"
                  fullWidth
                  value={form.fancyIntensity}
                  onChange={(next) => setField('fancyIntensity', next)}
                  options={intensityOptions}
                  emptyLabel=""
                />
                <Box display="flex" gap={1}>
                  <SelectField
                    label={t('inventory.fancyColorHue')}
                    size="small"
                    sx={{ flex: 1 }}
                    value={form.fancyHue}
                    onChange={(next) => setField('fancyHue', next)}
                    options={hueOptions}
                    emptyLabel=""
                  />
                  <SelectField
                    label={t('inventory.fancyColorOvertone')}
                    size="small"
                    sx={{ flex: 1 }}
                    value={form.fancyOvertone}
                    onChange={(next) => setField('fancyOvertone', next)}
                    options={overtoneOptions}
                    emptyLabel="—"
                    includeEmptyOption
                  />
                </Box>
              </>
            ) : (
              <Box display="flex" gap={1}>
                <SelectField
                  label="Color"
                  size="small"
                  sx={{ flex: 1 }}
                  value={form.color}
                  onChange={(next) => setField('color', next)}
                  options={(filterOptions.colors || []).map((c) => ({ value: c, label: c }))}
                  emptyLabel=""
                />
              </Box>
            )}
            <Box display="flex" gap={1}>
              <SelectField
                label="Clarity"
                size="small"
                sx={{ flex: 1 }}
                value={form.clarity}
                onChange={(next) => setField('clarity', next)}
                options={(filterOptions.clarities || []).map((c) => ({ value: c, label: c }))}
                emptyLabel=""
              />
              <SelectField
                label="Lab"
                size="small"
                sx={{ flex: 1 }}
                value={form.lab || ''}
                onChange={(next) => setField('lab', next)}
                options={LAB_OPTIONS.map((lab) => ({ value: lab, label: lab }))}
                emptyLabel=""
              />
            </Box>
            <Box display="flex" gap={1}>
              <SelectField
                label="Cut"
                size="small"
                sx={{ flex: 1 }}
                value={form.cut}
                onChange={(next) => setField('cut', next)}
                options={(filterOptions.cuts || []).map((c) => ({ value: c, label: c }))}
                emptyLabel=""
              />
              <SelectField
                label="Symmetry"
                size="small"
                sx={{ flex: 1 }}
                value={form.symmetry}
                onChange={(next) => setField('symmetry', next)}
                options={DIAMOND_GRADE_OPTIONS.map((s) => ({ value: s, label: s }))}
                emptyLabel="—"
                includeEmptyOption
              />
              <SelectField
                label="Polish"
                size="small"
                sx={{ flex: 1 }}
                value={form.polish}
                onChange={(next) => setField('polish', next)}
                options={DIAMOND_GRADE_OPTIONS.map((p) => ({ value: p, label: p }))}
                emptyLabel="—"
                includeEmptyOption
              />
            </Box>
            <Box display="flex" gap={1}>
              <TextField label="Length (mm)" type="number" value={form.length} onChange={(e) => setField('length', e.target.value)} inputProps={{ step: 0.01, min: 0 }} size="small" sx={{ flex: 1 }} />
              <TextField label="Width (mm)" type="number" value={form.width} onChange={(e) => setField('width', e.target.value)} inputProps={{ step: 0.01, min: 0 }} size="small" sx={{ flex: 1 }} />
              <TextField label="Height (mm)" type="number" value={form.height} onChange={(e) => setField('height', e.target.value)} inputProps={{ step: 0.01, min: 0 }} size="small" sx={{ flex: 1 }} />
            </Box>
            <Box display="flex" gap={1}>
              <TextField label="Base price" type="number" value={form.basePrice} onChange={(e) => setField('basePrice', e.target.value)} inputProps={{ step: 0.01, min: 0 }} size="small" sx={{ flex: 1 }} />
              {canSeeBuyPrices && (
                <TextField label="Buy price per carat" type="number" value={form.buyPricePerCarat} onChange={(e) => setField('buyPricePerCarat', e.target.value)} inputProps={{ step: 0.01, min: 0 }} size="small" sx={{ flex: 1 }} />
              )}
            </Box>
            <SelectField
              label="Status"
              fullWidth
              size="small"
              value={form.status}
              onChange={(next) => setField('status', next)}
              options={DIAMOND_STATUS_OPTIONS}
              emptyLabel=""
            />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('inventory.cancel')}</Button>
        <Button onClick={handleSave} variant="contained" disabled={!form || loading}>{t('inventory.save')}</Button>
      </DialogActions>
    </Dialog>
  );
}
